/* ottr-hero3d — two rigid layers, one scroll, no shader.
 *
 * A depth-map version tore, because displacing a pixel leaves a hole a
 * photograph has nothing to fill. This one never moves a pixel relative to its
 * neighbours; it moves two layers:
 *
 *   plate    the photograph WITH THE SUBJECT REMOVED, clipped by the card
 *   cut-out  the subject on a real alpha channel, clipped only at the top
 *
 * Removing the subject from the plate is the point. While the plate still
 * contained her there were two of her on screen, perfectly registered at rest
 * and separating the instant anything moved — which read as a smeared double
 * rather than as depth. The hole she leaves is filled by bleeding the
 * surrounding colour inward (a pull-push pyramid), so what sits behind her is
 * the scene's own light, soft, the way a defocused background should be.
 *
 * EVERY SHAPE VALUE IS READ FROM A CUSTOM PROPERTY, never from a data
 * attribute — see layout(). That is what lets a media query change the hero's
 * geometry per breakpoint.
 */
(function () {
  'use strict';

  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var smooth = function (t) { return t * t * (3 - 2 * t); };
  var clamp01 = function (v) { return v < 0 ? 0 : v > 1 ? 1 : v; };
  var num = function (cs, name, fallback) {
    var v = parseFloat(cs.getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  };

  /* The subject knocked out of the photograph, the hole filled from its own
     edges. Returns false if the browser will not give us a 2D context. */
  function buildPlate(canvas, photo, cut) {
    var nw = photo.naturalWidth || photo.width;
    var nh = photo.naturalHeight || photo.height;
    if (!nw || !nh) return false;

    var w = Math.min(nw, 1800);
    var h = Math.max(1, Math.round(w * (nh / nw)));

    var base = document.createElement('canvas');
    base.width = w; base.height = h;
    var b = base.getContext('2d');
    if (!b) return false;

    b.drawImage(photo, 0, 0, w, h);

    /* Over-erase by a few pixels: the matte's own soft edge would otherwise
       leave a rim of her in the plate, which is the ghost we are here to kill. */
    b.globalCompositeOperation = 'destination-out';
    var d = Math.max(2, Math.round(w * 0.004));
    for (var i = -2; i <= 2; i++) {
      for (var j = -2; j <= 2; j++) b.drawImage(cut, i * d, j * d, w, h);
    }
    b.globalCompositeOperation = 'source-over';

    /* Pull: halve until a single pixel, so every level bleeds colour one pixel
       further into the hole and the top level is solid. */
    var levels = [], cur = base, guard = 0;
    while ((cur.width > 1 || cur.height > 1) && guard++ < 20) {
      var cw = Math.max(1, Math.ceil(cur.width / 2));
      var ch = Math.max(1, Math.ceil(cur.height / 2));
      var c = document.createElement('canvas');
      c.width = cw; c.height = ch;
      var x = c.getContext('2d');
      x.imageSmoothingEnabled = true;
      if ('imageSmoothingQuality' in x) x.imageSmoothingQuality = 'high';
      x.drawImage(cur, 0, 0, cw, ch);
      levels.push(c);
      cur = c;
    }

    /* Push: each coarse level fills only what the finer one left transparent. */
    for (var k = levels.length - 1; k > 0; k--) {
      var dst = levels[k - 1], dx = dst.getContext('2d');
      dx.globalCompositeOperation = 'destination-over';
      dx.drawImage(levels[k], 0, 0, dst.width, dst.height);
      dx.globalCompositeOperation = 'source-over';
    }
    if (levels.length) {
      b.globalCompositeOperation = 'destination-over';
      b.drawImage(levels[0], 0, 0, w, h);
      b.globalCompositeOperation = 'source-over';
    }

    canvas.width = w; canvas.height = h;
    var out = canvas.getContext('2d');
    if (!out) return false;
    out.drawImage(base, 0, 0);
    return true;
  }

  function ready(img) {
    return new Promise(function (res) {
      if (!img) return res(null);
      if (img.complete && img.naturalWidth) return res(img);
      img.addEventListener('load', function () { res(img); }, { once: true });
      img.addEventListener('error', function () { res(null); }, { once: true });
    });
  }

  class OttrHero3D extends HTMLElement {
    connectedCallback() {
      if (this.booted) return;
      this.booted = true;

      this.root = this.closest('.ottr-hero3d');
      this.stage = this.querySelector('[data-stage]');
      this.scroller = this.querySelector('.ottr-hero3d__scroller');
      this.sticky = this.querySelector('.ottr-hero3d__sticky');
      this.photo = this.querySelector('[data-photo]');
      this.plate = this.querySelector('[data-plate]');
      this.fgimg = this.querySelector('[data-fgimg]');
      if (!this.stage || !this.sticky) return;

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.push = (+this.dataset.push || 12) / 100;
      /* How far the cut-out already overflows the card before you scroll. The
         subject starts OUT of the frame, so even the rest state reads as depth
         rather than as a photograph sitting in a box. */
      this.fgOut = (+this.dataset.out || 34) / 100;
      /* ...and lifted, so what breaks the top edge is the product rather than
         her hair. Kept small enough that the bottom of the cut-out still covers
         the bottom of the hole it was erased from. */
      this.fgRise = (+this.dataset.rise || 11) / 100;
      /* ...and what is left of that oversize once the card IS the screen. The
         photograph is framed tight on the product, so any oversize at full
         bleed comes straight out of the buffer above and below it. Small. */
      this.fgEnd = (+this.dataset.fgend || 6) / 100;
      this.tilt = (+this.dataset.tilt || 100) / 100;
      /* fraction of the runway spent opening out; the rest is held open so the
         sequence ends still rather than mid-transition */
      this.openBy = Math.min(0.95, Math.max(0.2, (+this.dataset.openby || 90) / 100));
      /* where in the opening the copy arrives — the same move that turns the
         mark white */
      this.copyAt = Math.min(0.9, Math.max(0, (+this.dataset.copyat || 45) / 100));

      this.mx = 0; this.my = 0; this.cx = 0; this.cy = 0;

      var self = this;
      Promise.all([ready(this.photo), ready(this.fgimg)]).then(function (r) {
        self.go();
        if (self.plate && r[0] && r[1]) {
          try {
            if (buildPlate(self.plate, r[0], r[1])) self.root.classList.add('is-plated');
          } catch (e) { /* the photograph alone is a correct, duller hero */ }
        }
      });
    }

    go() {
      if (this.live) return;
      this.live = true;
      var self = this;
      this.root.classList.add('is-live');
      this.layout();

      this.onResize = function () { self.layout(); };
      addEventListener('resize', this.onResize);
      addEventListener('load', this.onResize);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.onResize);
        this.ro.observe(this.sticky);
      }

      if (!this.reduced) {
        this.onMove = function (ev) {
          var r = self.sticky.getBoundingClientRect();
          if (!r.width || !r.height) return;
          self.mx = ((ev.clientX - r.left) / r.width - 0.5) * 2;
          self.my = ((ev.clientY - r.top) / r.height - 0.5) * 2;
        };
        this.onLeave = function () { self.mx = 0; self.my = 0; };
        addEventListener('pointermove', this.onMove, { passive: true });
        this.sticky.addEventListener('pointerleave', this.onLeave);
      }

      /* A frame loop, not a scroll listener: this theme scrolls .page-wrapper,
         not the window, so a window scroll listener never fires and the hero
         renders one frame and stops. The loop also carries the pointer easing,
         which needs frames anyway. */
      this.io = new IntersectionObserver(function (es) {
        self.visible = es[0].isIntersecting;
        if (self.visible) self.start(); else self.running = false;
      }, { rootMargin: '12% 0px' });
      this.io.observe(this.root);
      this.visible = true;
      this.start();
    }

    disconnectedCallback() {
      this.running = false;
      if (this.onResize) {
        removeEventListener('resize', this.onResize);
        removeEventListener('load', this.onResize);
      }
      if (this.onMove) removeEventListener('pointermove', this.onMove);
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
    }

    /* Every shape value is read from a CUSTOM PROPERTY, never from a data
       attribute, so a media query can change it. The section's settings are
       written to --h3d-set-* on the element and the base rule copies them
       across; breakpoints then override the plain names and win, because both
       live in the stylesheet. An inline style would beat any media query —
       which is precisely why the phone rules did nothing for several rounds. */
    layout() {
      var cs = getComputedStyle(this.root);
      var W = this.sticky.clientWidth;
      var H = this.sticky.clientHeight;
      if (!W || !H) return;
      this.sw = W; this.fullH = H;

      this.aspect = num(cs, '--h3d-aspect', 175) / 100;
      this.rad0 = num(cs, '--h3d-radius', 26);

      /* fill 1: the card opens out to the whole screen.
         fill 0: it opens to the full WIDTH at --h3d-end-aspect and centres,
         with page above and below. On a portrait viewport a cover crop of a
         landscape photograph throws away most of its width, so full bleed
         there would end on a narrow slice of torso. */
      this.fill = num(cs, '--h3d-fill', 1);
      var endAspect = num(cs, '--h3d-end-aspect', 0) / 100;

      /* The resting card keeps a fixed shape and is fitted to the whole pinned
         area. Sizing each axis independently collapsed to a letterbox slit on a
         laptop and cover-cropped the photograph to a sliver of hand and torso. */
      var maxW = W * num(cs, '--h3d-card-w', 46) / 100;
      var maxH = H * num(cs, '--h3d-card-h', 48) / 100;
      this.cw0 = Math.min(maxW, maxH * this.aspect);
      this.ch0 = this.cw0 / this.aspect;

      this.ew = W;
      this.eh = this.fill ? H : (endAspect ? Math.min(H, W / endAspect) : H);
      this.dirty = true;
    }

    progress() {
      var r = this.scroller.getBoundingClientRect();
      var run = this.scroller.offsetHeight - this.sticky.offsetHeight;
      if (run <= 0) return 1;
      /* viewport-relative, whichever element happens to scroll */
      return clamp01(-r.top / run);
    }

    start() {
      if (this.running) return;
      this.running = true;
      var self = this;
      (function loop() {
        if (!self.running || !self.visible) { self.running = false; return; }
        self.frame();
        requestAnimationFrame(loop);
      })();
    }

    frame() {
      if (!this.sw) { this.layout(); return; }
      /* With motion reduced there is no scroll story to tell, so show its
         resolved state: open, copy present. */
      var t = this.reduced ? 1 : this.progress();
      var e = 1 - Math.pow(1 - t, 2.2);

      this.cx = lerp(this.cx, this.mx, 0.07);
      this.cy = lerp(this.cy, this.my, 0.07);

      var key = (e * 2000 | 0) + ':' + (this.cx * 300 | 0) + ':' + (this.cy * 300 | 0);
      if (key === this.key && !this.dirty) return;
      this.key = key; this.dirty = false;

      /* the card opens out and squares its corners */
      var k = smooth(Math.min(1, e / this.openBy));
      /* and the copy arrives partway through that, with the mark turning white */
      var c = smooth(clamp01((k - this.copyAt) / (1 - this.copyAt)));

      var s = this.root.style;
      var ch = lerp(this.ch0, this.eh, k);
      s.setProperty('--h3d-cw', lerp(this.cw0, this.ew, k).toFixed(1) + 'px');
      s.setProperty('--h3d-ch', ch.toFixed(1) + 'px');
      s.setProperty('--h3d-cy', (this.fullH / 2).toFixed(1) + 'px');
      /* corners only square off if it is actually going full bleed */
      s.setProperty('--h3d-rad', (this.rad0 * (1 - k * this.fill)).toFixed(1) + 'px');
      s.setProperty('--h3d-open', k.toFixed(3));
      s.setProperty('--h3d-copy', c.toFixed(3));

      if ((c > 0.02) !== !!this.copyOn) {
        this.copyOn = c > 0.02;
        this.root.classList.toggle('has-copy', this.copyOn);
      }

      /* The plate barely moves. All the travel belongs to the cut-out — that
         difference IS the depth; a plate that moves with it reads as zoom. */
      s.setProperty('--h3d-bg-scale', (1 + this.push * 0.18 * e).toFixed(4));
      s.setProperty('--h3d-fg-scale', (1 + lerp(this.fgOut, this.fgEnd, k)).toFixed(4));
      /* The rise is handed back as the card opens: by then the frame is the
         screen, so there is no edge left to break and the product would only be
         walking off the top. */
      s.setProperty('--h3d-fg-y', (-this.fgRise * ch * (1 - k)).toFixed(2) + 'px');
      s.setProperty('--h3d-px', (this.cx * 7 * this.tilt).toFixed(2) + 'px');
      s.setProperty('--h3d-py', (this.cy * 4.5 * this.tilt).toFixed(2) + 'px');
      s.setProperty('--h3d-lift', e.toFixed(3));
    }
  }

  if (!customElements.get('ottr-hero3d')) {
    customElements.define('ottr-hero3d', OttrHero3D);
  }
})();
